/**
 * The saved filters' query language, on the screen's side (migration 074).
 *
 * THE SERVER PARSES. backend/lib/todo-filter-query.js is the one parser: it
 * turns the text into a tree, checks it, and runs it as parameterised SQL.
 * This file never parses a query. What it does is the other half of the
 * builder:
 *
 *   FIELDS        what each field is called and which controls it gets
 *   toModel()     the server's tree (POST /todos/filters/preview answers it)
 *                 -> the builder's rows and groups
 *   fromModel()   the builder -> a tree
 *   print()       a tree -> text, spelled exactly as the server prints it
 *                 (lib/todo-filter-query print), so a query made in the
 *                 builder reads back as the same tree - e2e/todo-filters.mjs
 *                 feeds what this prints to the real parser to hold that.
 *
 * A BUILDER ROW is { id, kind: 'cond', field, op, value, named, negate } - a
 * condition, "is not" when negate. A GROUP is { id, kind: 'group', mode:
 * 'all' | 'any', negate, items } - "All of", "Any of", and with negate "None
 * of" / "Not all of". The top of the builder is always a group.
 *
 * A yes/no field (Flagged, Waiting...) shows its answer as the value, not as
 * "is not": "!flagged" comes into the builder as Flagged: no, and goes back
 * out as flagged:no - the same to-dos, in the builder's words.
 */

/* ------------------------------------------------------------- the fields */

export const DATE_WORDS = [
  ['today', 'today'], ['tomorrow', 'tomorrow'], ['overdue', 'overdue'], ['this_week', 'this week'],
  ['next_week', 'next week'], ['next_7_days', 'in the next 7 days'], ['none', 'not set'], ['any', 'set']
];
export const DATE_OPS = [['<', 'before'], ['>', 'after'], ['on', 'on'], ['<=', 'on or before'], ['>=', 'on or after']];
export const DATE_VALUES = [['today', 'today'], ['tomorrow', 'tomorrow'], ['yesterday', 'yesterday'],
  ['+1w', 'a week from today'], ['-1w', 'a week ago']];
export const DURATION_OPS = [['>', 'more than'], ['>=', 'at least'], ['<', 'less than'], ['<=', 'at most'], ['on', 'exactly']];

/**
 * What the builder offers for each field: its name on screen, its kind (which
 * controls), the words it takes besides names, and how "is" and "is not" read.
 */
export const FIELDS = {
  priority: { label: 'Priority', kind: 'priority', is: ['is', 'is not'] },
  label: { label: 'Label', kind: 'name', words: [['any', 'any label'], ['none', 'no label']], is: ['is', 'is not'] },
  list: { label: 'List', kind: 'name', words: [], is: ['is', 'is not'] },
  section: { label: 'Section', kind: 'name', words: [['none', 'no section']], is: ['is', 'is not'] },
  client: { label: 'Client', kind: 'name', words: [['any', 'any client'], ['none', 'no client']], is: ['is', 'is not'] },
  assignee: { label: 'Assigned to', kind: 'name',
              words: [['me', 'me'], ['none', 'nobody'], ['others', 'somebody else'], ['any', 'anybody']], is: ['is', 'is not'] },
  created_by: { label: 'Created by', kind: 'name', words: [['me', 'me'], ['others', 'somebody else']], is: ['is', 'is not'] },
  due: { label: 'Date', kind: 'date', is: ['is', 'is not'] },
  deadline: { label: 'Deadline', kind: 'date', is: ['is', 'is not'] },
  duration: { label: 'Duration', kind: 'duration', is: ['is', 'is not'] },
  flagged: { label: 'Flagged', kind: 'bool' },
  waiting: { label: 'Waiting on another to-do', kind: 'bool' },
  blocking: { label: 'Holding up another to-do', kind: 'bool' },
  repeating: { label: 'Repeating', kind: 'bool' },
  shared: { label: 'In a shared list', kind: 'bool' },
  ticket: { label: 'Ticket', kind: 'presence', is: ['is', 'is not'] },
  search: { label: 'Words', kind: 'text', is: ['contain', 'don’t contain'] }
};

/** A fresh condition for a field, ready to be filled in. */
export function blankCond(field) {
  const f = FIELDS[field] || FIELDS.priority;
  const base = { id: uid(), kind: 'cond', field, negate: false, named: false };
  switch (f.kind) {
    case 'priority': return { ...base, op: 'is', value: '1' };
    case 'name': return { ...base, op: 'is', value: field === 'assignee' || field === 'created_by' ? 'me' : '', named: false };
    case 'date': return { ...base, op: 'is', value: 'today' };
    case 'duration': return { ...base, op: '>=', value: '60' };
    case 'bool': return { ...base, op: 'is', value: 'yes' };
    case 'presence': return { ...base, op: 'is', value: 'any' };
    default: return { ...base, op: 'is', value: '' };
  }
}

let n = 0;
const uid = () => { n += 1; return `q${n}`; };

export const blankGroup = (mode = 'all', items = []) => ({ id: uid(), kind: 'group', mode, negate: false, items });

/* --------------------------------------------------- the tree <-> the builder */

/** The server's tree -> a builder group. */
export function toModel(tree) {
  if (!tree) return blankGroup();
  const top = item(tree);
  return top.kind === 'group' ? top : blankGroup('all', [top]);
}

function item(node) {
  if (node.type === 'cond') return cond(node, false);
  if (node.type === 'not') {
    const inner = node.item;
    if (inner.type === 'not') return item(inner.item);            // !!x is x
    if (inner.type === 'cond') return cond(inner, true);
    return { ...group(inner), negate: true };
  }
  return group(node);
}

function group(node) {
  return { id: uid(), kind: 'group', mode: node.type === 'or' ? 'any' : 'all', negate: false, items: node.items.map(item) };
}

function cond(c, negate) {
  const f = FIELDS[c.field] || {};
  const row = { id: uid(), kind: 'cond', field: c.field, op: c.op, value: String(c.value), named: Boolean(c.named), negate };
  // A yes/no answer carries its own "not".
  if (f.kind === 'bool' && negate) return { ...row, negate: false, value: c.value === 'yes' ? 'no' : 'yes' };
  return row;
}

/** Is a row filled in enough to be part of the query? */
export const complete = (r) => r.kind === 'group' ? r.items.some(complete) : String(r.value ?? '').trim() !== '';

/** A builder group -> a tree, leaving out rows not yet filled in. null when there is nothing. */
export function fromModel(g) {
  if (g.kind === 'cond') {
    if (!complete(g)) return null;
    const c = { type: 'cond', field: g.field, op: g.op, value: String(g.value).trim() };
    if (g.named) c.named = true;
    return g.negate ? { type: 'not', item: c } : c;
  }
  const items = g.items.map(fromModel).filter(Boolean);
  if (!items.length) return null;
  const node = items.length === 1 ? items[0] : { type: g.mode === 'any' ? 'or' : 'and', items };
  return g.negate ? { type: 'not', item: node } : node;
}

/* ---------------------------------------------------------- printing */

/* The server's print(), line for line: change one, change both. */

const NAME_WORDS = { label: ['none', 'any'], list: [], section: ['none'], client: ['none', 'any'],
                     assignee: ['me', 'none', 'any', 'others'], created_by: ['me', 'others'] };

const quoteAlways = (s) => `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
const quote = (s) => (/^[^\s&|!()"]+$/.test(s) && !/^(<=|>=|<|>|=)/.test(s) && !/:/.test(s) ? s : quoteAlways(s));

/** Minutes as the server writes them: 30m, 2h, 1h30m. */
export const minutesText = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (!h) return `${mm}m`;
  return mm ? `${h}h${mm}m` : `${h}h`;
};

function condText(c) {
  const { field, op, value: v } = c;
  const f = FIELDS[field];
  switch (f && f.kind) {
    case 'priority': return op === 'is' ? `p${v}` : `priority:${op}${v}`;
    case 'name': {
      const words = NAME_WORDS[field] || [];
      const word = words.includes(v) && !c.named;
      if (field === 'label' && !word) return `@${quote(v)}`;
      if (field === 'list') return `#${quote(v)}`;
      if (field === 'assignee' && word && v === 'none') return 'unassigned';
      if (field === 'label' && word && v === 'none') return 'no label';
      if (word) return `${field}:${v}`;
      return `${field}:${words.includes(String(v).toLowerCase()) ? quoteAlways(v) : quote(v)}`;
    }
    case 'bool': return v === 'yes' ? field : `${field}:no`;
    case 'presence': return `${field}:${v}`;
    case 'text': return `search:${quote(v)}`;
    case 'date': {
      if (op === 'is') {
        if (field === 'due' && ['today', 'tomorrow', 'overdue'].includes(v)) return v;
        if (v === 'none') return field === 'due' ? 'no date' : `no ${field}`;
        return `${field}:${v}`;
      }
      return `${field}:${{ '<': 'before:', '>': 'after:', on: 'on:' }[op] || op}${v}`;
    }
    case 'duration': {
      if (op === 'is') return `duration:${v}`;
      return `duration:${op === 'on' ? '=' : op}${minutesText(Number(v))}`;
    }
    default: return `${field}:${quote(v)}`;
  }
}

const PREC = { or: 1, and: 2, not: 3, cond: 4 };

/** A tree -> its text. */
export function print(node) {
  if (!node) return '';
  const wrap = (child, min) => (PREC[child.type] < min ? `(${print(child)})` : print(child));
  switch (node.type) {
    case 'or': return node.items.map((x) => wrap(x, PREC.or)).join(' | ');
    case 'and': return node.items.map((x) => wrap(x, PREC.and + 0.5)).join(' & ');
    case 'not': return `!${wrap(node.item, PREC.not)}`;
    default: return condText(node);
  }
}

/** The builder straight to text. */
export const modelText = (g) => print(fromModel(g));

/** How deep a builder group goes - the builder draws three levels. */
export const depthOf = (g) => (g.kind === 'group' ? 1 + Math.max(0, ...g.items.map(depthOf)) : 0);

/* ------------------------------------------------------------ in words */

const dateWords = (field, op, v) => {
  const when = (x) => (DATE_VALUES.find(([k]) => k === x)?.[1]) || x;
  if (op === 'is') return (DATE_WORDS.find(([k]) => k === v)?.[1]) || v;
  return `${(DATE_OPS.find(([k]) => k === op)?.[1]) || op} ${when(v)}`;
};

/** One condition as a short phrase, for the filter list: "Date is today". */
export function condWords(c) {
  const f = FIELDS[c.field];
  if (!f) return c.field;
  const not = c.negate;
  switch (f.kind) {
    case 'priority':
      if (c.op === 'is') return `${not ? 'Not ' : ''}P${c.value}`;
      return `P${c.value} or ${c.op.startsWith('<') ? 'higher' : 'lower'}`;
    case 'bool': return `${f.label}: ${c.value}`;
    case 'date': return `${f.label} ${not ? 'is not ' : ''}${dateWords(c.field, c.op, c.value)}`;
    case 'duration':
      if (c.op === 'is') return `Duration ${c.value === 'none' ? 'not set' : 'set'}`;
      return `Takes ${(DURATION_OPS.find(([k]) => k === c.op)?.[1]) || c.op} ${minutesText(Number(c.value))}`;
    default: {
      const word = (f.words || []).find(([k]) => k === c.value && !c.named);
      return `${f.label} ${not ? f.is[1] : f.is[0]} ${word ? word[1] : `“${c.value}”`}`;
    }
  }
}

/* ------------------------------------------------- starting points */

/**
 * The few one-click starting points an empty Filters page offers, each only
 * where this server can answer it (`fields` from GET /todos/filters).
 */
export const SUGGESTIONS = [
  { key: 'p1-week', name: 'P1 due this week', query: 'p1 & (due:this_week | overdue)', color: 'red', icon: 'flag',
    needs: [], text: 'Priority 1, due this week or already late' },
  { key: 'waiting', name: 'Waiting on someone', query: 'waiting | assignee:others', color: 'orange', icon: 'clock',
    needs: ['waiting', 'assignee'], text: 'Held up by another to-do, or with somebody else' },
  { key: 'client-nodate', name: 'Client work without a date', query: 'client:any & no date', color: 'blue', icon: 'briefcase',
    needs: ['client'], text: 'For a client, but nobody has said when' },
  { key: 'long-today', name: 'Long tasks today', query: '(today | overdue) & duration:>=1h', color: 'purple', icon: 'today',
    needs: ['duration'], text: 'An hour or more, on today’s plate' },
  { key: 'flagged-late', name: 'Flagged and late', query: 'flagged & overdue', color: 'yellow', icon: 'flag',
    needs: ['flagged'], text: 'Flagged, and past its date' },
  { key: 'mine-nodate', name: 'Mine, not yet planned', query: 'assignee:me & no date', color: 'teal', icon: 'person',
    needs: ['assignee'], text: 'Given to me, with no day to do it' }
];

/** The suggestions this server can answer, at most four. */
export const suggestionsFor = (fields) => SUGGESTIONS
  .filter((s) => s.needs.every((f) => (fields || []).includes(f))).slice(0, 4);
